import _ from 'lodash';
import moment from 'moment';

export const OrdersComponent = {
    template: `
        <oo-content>
            <oo-content-header class="hide-sm">
                My Orders
            </oo-content-header>
            <oo-content-body>
                <oo-tabs oo-theme="light">
                    <oo-tab title="Past Orders">
                        <oo-card ng-if="!$ctrl.getHasDisplayOrders()">
                            <oo-card-body>
                                <div class="column-sm-12 column no-orders-container">
                                    <div class="glyph-container">
                                        <span class="glyph glyph-fork-spoon"></span>
                                    </div>
                                    <p c-type="title-md-primary">No orders yet!</p>
                                    <p c-type="title-xs-primary">Place an order to add to this list.</p>
                                </div>
                            </oo-card-body>
                        </oo-card>
                        <oo-card ng-repeat="order in $ctrl.data.displayOrders">
                            <oo-card-body class="flex-end">
                                <div class="column-sm-12 column expand">
                                    <p c-type="title-lg-primary bold">{{ order.date | moment: 'MMMM D, YYYY' }}</p>
                                    <p c-type="title-lg-primary">{{ order.items }}</p>
                                    <p c-type="title-lg-primary" class="price pull-right">{{ order.price | currency }}</p>
                                    <p c-type="body-md-primary bold" ng-if="order.diningOption">{{ order.diningOption }}</p>
                                    <p c-type="body-lg-primary" ng-if="order.address">{{ order.address }}</p>
                                </div>
                                <oo-button
                                    class="reorder-button"
                                    types="button-primary"
                                    button-click="$ctrl.reorder($index)"
                                    is-loading="$ctrl.isReordering[$index]"
                                    ga-track-event="$ctrl.reorderEvent"
                                >Reorder</oo-button>
                            </oo-card-body>
                        </oo-card>
                    </oo-tab>
                </oo-tabs>
            </oo-content-body>
        </oo-content>`,
    controller: class OrdersComponent {

        constructor(accountService, cartService, cartDisplayService, cartStatusChannel, featureFlagService, $state, $q, restaurantInfoFactory, googleAnalyticsTrackingService) {
            'ngInject';
            this.accountService = accountService;
            this.cartService = cartService;
            this.cartStatusChannel = cartStatusChannel;
            this.cartDisplayService = cartDisplayService;
            this.featureFlagService = featureFlagService;
            this.$state = $state;
            this.$q = $q;
            this.data = accountService.data;
            this.restaurantInfoFactory = restaurantInfoFactory;
            this.isReordering = [];
            this.reorderEvent = googleAnalyticsTrackingService.getReorderEvent();
        }

        $onInit() {
            this.restaurantInfoFactory.getInfo(['general'])
                .then(data => this.accountService.getOrders(data.general.guid))
                .then(
                    () => { this.data.displayOrders = OrdersComponent.formatOrders(this.data.orders); },
                    () => { this.$state.go('order'); }
                );
        }

        getHasDisplayOrders() {
            return this.data.displayOrders && this.data.displayOrders.length > 0;
        }

        reorder(index) {
            this.isReordering[index] = true;
            const menuItems = this._getMenuItemsForReorder(index);

            this._addMenuItemsToCart(menuItems).then(
                () => this._tryFinishReorder(index),
                () => {
                    this.cartService.setCartError({
                        errorMessage: 'Not all items you selected are available. Please make another selection.'
                    });
                    this._tryFinishReorder(index);
                }
            );
        }

        /**
         * Returns menu items corresponding to the given order index that are suitable
         * to be passed in to `cartService.addCartItem()`.
         *
         * @param {Number} index
         * @returns {Array} a copy of the menu items for the given order
         */
        _getMenuItemsForReorder(index) {
            const menuItems = _.cloneDeep(this.data.orders[index].menuItems);
            _.forEach(menuItems, menuItem => this._fillMenuItemGuids(menuItem));
            return menuItems;
        }

        /**
         * Modifies `menuItem` in-place to fill in the various guid fields needed when
         * calling `cartService.addCartItem()` for a G2 cart.
         *
         * This is necessary because toast-customermgmt has yet another slightly different
         * data structure that sort of looks like a G1 cart, but not exactly...
         *
         * This could be done in `accountService.getOrders()` if we want to put
         * it in the `CustomerAuthentication` library instead.
         *
         * @param {*} menuItem a menu item or modifier as returned from toast-customermgmt
         * @returns {void}
         */
        _fillMenuItemGuids(menuItem) {
            menuItem.guid = menuItem.guidString || menuItem.itemReferenceGuidString;

            if (menuItem.menuGroupGuidString) {
                menuItem.groupGuid = menuItem.menuGroupGuidString;
            }

            if (menuItem.itemGroupGuidString) {
                menuItem.itemGroupGuid = menuItem.itemGroupGuidString;
            }

            _.forEach(menuItem.modifierGroups || [], modifierGroup => {
                modifierGroup.guid = modifierGroup.guidString;

                _.forEach(modifierGroup.modifiers || [], modifier => this._fillMenuItemGuids(modifier));
            });
        }

        /**
         *
         * @param {Array} menuItems
         * @returns {Promise<void>}
         */
        _addMenuItemsToCart(menuItems) {
            return this._serialAddMenuItemsToCart(menuItems);
        }

        /**
         * `_legacyParallelAddMenuItemsToCart()` inherently creates race conditions since it will
         * try to add every item from the past order simultaneously via `$q.all()`.  G2 actively
         * locks the cart on the backend so we will get a 409 Conflict response if we try to
         * perform multiple operations at once.
         */
        _serialAddMenuItemsToCart(menuItems) {
            const callback = (lastPromise, menuItem) =>
                lastPromise.then(() => this.cartService.addCartItem(menuItem, true));
            return _.reduce(menuItems, callback, this.$q.resolve());
        }

        _tryFinishReorder(index) {
            this.isReordering[index] = false;
            this.cartDisplayService.updateFulfillmentTimeFromCart();
            this.cartStatusChannel.changedCart();
            this.$state.go('order.cart');
        }

        static formatOrders(orders) {
            return orders.map(order => ({
                date: isNaN(order.openedDate) ? moment(order.openedDate) : moment.unix(order.openedDate / 1000), // TODO: remove isNaN and unix after customermgmt 31 is live
                items: OrdersComponent.formatMenuItems(order.menuItems),
                address: order.address ? OrdersComponent.formatAddress(order.address) : null,
                price: order.price,
                diningOption: order.diningOption
            }));
        }

        static formatMenuItems(menuItems) {
            return menuItems.map(menuItem =>
                menuItem.displayName + OrdersComponent.formatModifierGroups(menuItem.modifierGroups, true)
            ).join(', ');
        }

        static formatModifierGroups(modifierGroups, outermost) {
            const s = modifierGroups.map(modifierGroup =>
                modifierGroup.modifiers.map(
                    modifier => modifier.displayName + OrdersComponent.formatModifierGroups(modifier.modifierGroups, false)
                ).join(', ')
            ).join(', ');
            if (outermost) {
                return s ? ` (${s})` : '';
            } else {
                return s ? ` - ${s}` : '';
            }
        }

        // TODO Single-source with ReadOnlyCartController. Need to resolve state vs stateCode.
        static formatAddress(address) {
            var address2 = address.address2;
            if (address2) {
                address2 += ', ';
            } else {
                address2 = '';
            }
            return address.address1 + ', ' + address2 + address.city + ', ' + address.state + ' ' + address.zipCode;
        }

    }
};
